import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, resolvedSupabaseConfig } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import WizardService from '../../../services/wizardService';

const CURRENCY_OPTIONS = ['AUD', 'CAD', 'USD', 'EUR', 'GBP', 'LKR'];

const createLocalId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `local-${Math.random().toString(36).slice(2, 11)}`;
};

const AgreementsBuilderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get('submission') || null;
  const clientIdFromQuery = searchParams.get('client') || null;
  const caseIdFromQuery = searchParams.get('case') || null;
  const pathwayIdFromQuery = searchParams.get('pathway') || null;

  const { profile, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loanOptions, setLoanOptions] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [sections, setSections] = useState([]);

  // CHANGED: Instead of syncing 'variables', we only store user overrides.
  // The final value is derived on-the-fly from defaults + overrides.
  const [overriddenVariables, setOverriddenVariables] = useState({});

  const [agreementMeta, setAgreementMeta] = useState({
    title: '',
    currency: 'AUD',
    notes: '',
    validUntil: '',
    acceptance_deadline: '',
    payment_deadline: ''
  });
  const [recipientRows, setRecipientRows] = useState([]);
  const [items, setItems] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [client, setClient] = useState(null);
  const [errors, setErrors] = useState([]);
  const [infoMessage, setInfoMessage] = useState('');
  const [loadStatus, setLoadStatus] = useState({
    templates: 'idle',
    clauses: 'idle',
    packages: 'idle',
    loans: 'idle',
    case: 'idle',
    submission: 'idle',
    client: 'idle'
  });
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [selectedLoanId, setSelectedLoanId] = useState('');
  const [customItem, setCustomItem] = useState({
    title: '',
    quantity: 1,
    unitPrice: '',
    description: ''
  });
  const [clauseSearch, setClauseSearch] = useState('');
  const [caseContext, setCaseContext] = useState(null);

  const totalAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unit = Number(item.unitPrice) || 0;
      return sum + quantity * unit;
    }, 0);
  }, [items]);

  const deriveVariableDefault = useCallback(
    (key) => {
      const safeKey = (key || '').toLowerCase();
      const personal = submission?.personal_info || {};
      const clientName = [
        client?.first_name,
        client?.last_name
      ].filter(Boolean).join(' ');
      if (safeKey === 'client_name' || safeKey === 'lead_name') {
        return clientName || personal.fullName || personal.name || [personal.firstName, personal.lastName].filter(Boolean).join(' ') || '';
      }
      if (safeKey === 'client_email') {
        return client?.primary_email || personal.email || '';
      }
      if (safeKey === 'client_phone') {
        return client?.primary_phone || personal.phone || '';
      }
      if (safeKey === 'advisor_name' || safeKey === 'prepared_by') {
        return profile?.name || profile?.full_name || profile?.email || user?.email || '';
      }
      if (safeKey === 'today_date' || safeKey === 'prepared_at') {
        return new Date().toLocaleDateString();
      }
      if (safeKey === 'agreement_title') {
        return agreementMeta.title || '';
      }
      if (safeKey === 'agreement_total' || safeKey === 'total_amount') {
        return totalAmount ? totalAmount.toFixed(2) : '0.00';
      }
      if (safeKey === 'currency') {
        return agreementMeta.currency || 'AUD';
      }
      if (safeKey === 'valid_until' || safeKey === 'expiry_date') {
        return agreementMeta.validUntil || '';
      }
      if (safeKey === 'submission_id') {
        return submission?.id || '';
      }
      return '';
    },
    [agreementMeta.currency, agreementMeta.title, agreementMeta.validUntil, client, profile, submission, totalAmount, user]
  );

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        setLoadStatus({
          templates: 'loading',
          clauses: 'loading',
          packages: 'loading',
          loans: 'loading',
          case: caseIdFromQuery ? 'loading' : 'idle',
          submission: submissionId ? 'loading' : 'idle',
          client: (submissionId || clientIdFromQuery) ? 'loading' : 'idle'
        });
        const tplPromise = supabase.from('agreement_templates').select('*').order('updated_at', { ascending: false });
        const clausePromise = supabase
          .from('clause_library')
          .select('*')
          .eq('is_active', true)
          .order('updated_at', { ascending: false });

        // Fetch packages with a safe query (no deep joins to avoid schema mismatch/RLS issues)
        let pkgRes = await supabase
          .from('packages')
          .select('id, package_name, package_key, price_range_min, price_range_max, currency, pathway_id, is_active')
          .eq('is_active', true)
          .order('package_name');
        if (pkgRes.error) {
          // Fallback to a minimal select if the ordered query fails
          pkgRes = await supabase.from('packages').select('id, package_name, package_key, price_range_min, price_range_max, currency, pathway_id, is_active').eq('is_active', true);
        }

        const loanPromise = supabase
          .from('loan_options')
          .select('id, loan_provider, loan_type, max_loan_amount, interest_rate, repayment_period_years, requirements, is_active')
          .eq('is_active', true)
          .order('loan_provider');

        const [tplRes, clauseRes, loanRes] = await Promise.all([tplPromise, clausePromise, loanPromise]);

        setLoadStatus((prev) => ({
          ...prev,
          templates: tplRes.error ? 'error' : 'loaded',
          clauses: clauseRes.error ? 'error' : 'loaded',
          packages: pkgRes.error ? 'error' : 'loaded',
          loans: loanRes.error ? 'error' : 'loaded'
        }));

        // Case context (if provided)
        let caseRow = null;
        if (caseIdFromQuery) {
          const { data: cRow } = await supabase
            .from('cases')
            .select(`
              id,
              title,
              pathway_id,
              clients ( first_name, last_name ),
              immigration_pathways:pathway_id (
                id,
                pathway_key,
                countries (
                  name,
                  flag_emoji
                )
              )
            `)
            .eq('id', caseIdFromQuery)
            .single();
          caseRow = cRow || null;
          setCaseContext(caseRow);
          setLoadStatus((prev) => ({ ...prev, case: caseRow ? 'loaded' : 'error' }));
        }

        setTemplates(tplRes.data || []);
        setClauses(clauseRes.data || []);
        setPackages(pkgRes.data || []);
        setLoanOptions(loanRes.data || []);

        let submissionRow = null;
        let clientRow = null;
        if (submissionId) {
          const { data: subData, error: subError } = await supabase
            .from('wizard_submissions')
            .select('id, personal_info, client_id, created_at')
            .eq('id', submissionId)
            .single();
          if (subError) throw subError;
          setLoadStatus((prev) => ({ ...prev, submission: 'loaded' }));
          submissionRow = subData || null;
          setSubmission(submissionRow);
          if (submissionRow?.client_id) {
            const { data: clientData } = await supabase
              .from('clients')
              .select('*')
              .eq('id', submissionRow.client_id)
              .single();
            if (clientData) {
              clientRow = clientData;
              setClient(clientRow);
            }
            setLoadStatus((prev) => ({ ...prev, client: clientRow ? 'loaded' : 'error' }));
          }
        } else if (clientIdFromQuery) {
          const { data: clientData, error: clientError } = await supabase
            .from('clients')
            .select('*')
            .eq('id', clientIdFromQuery)
            .single();
          if (clientError) throw clientError;
          clientRow = clientData || null;
          setClient(clientRow);
          setLoadStatus((prev) => ({ ...prev, client: clientRow ? 'loaded' : 'error' }));
        }

        const personal = submissionRow?.personal_info || {};
        let derivedTitle = agreementMeta.title;
        if (!derivedTitle) {
          const clientName = [clientRow?.first_name, clientRow?.last_name].filter(Boolean).join(' ');
          const leadName = personal.fullName || personal.name || [personal.firstName, personal.lastName].filter(Boolean).join(' ');

          if (clientName) {
            derivedTitle = `${clientName} Engagement`;
          } else if (leadName) {
            derivedTitle = `${leadName} Engagement`;
          } else {
            derivedTitle = 'New Service Agreement';
          }
        }

        const defaultValidUntil = (() => {
          const date = new Date();
          date.setDate(date.getDate() + 7);
          return date.toISOString().slice(0, 10);
        })();

        setAgreementMeta((prev) => ({
          ...prev,
          title: prev.title || derivedTitle,
          currency: prev.currency || (pkgRes.data?.[0]?.currency || 'AUD'),
          validUntil: prev.validUntil || defaultValidUntil
        }));

        // Initial recipient setup logic
        const defaultRecipientName =
          [clientRow?.first_name, clientRow?.last_name].filter(Boolean).join(' ') ||
          personal.fullName ||
          personal.name ||
          '';
        const defaultRecipientEmail = clientRow?.primary_email || personal.email || '';

        const staffDisplayName = profile?.name || profile?.full_name || profile?.display_name || user?.user_metadata?.full_name || user?.email || '';
        const staffEmail = profile?.email || profile?.work_email || user?.email || '';

        const initialRecipients = [];
        if (defaultRecipientEmail || defaultRecipientName) {
          initialRecipients.push({
            id: createLocalId(),
            role: 'client',
            full_name: defaultRecipientName,
            email: defaultRecipientEmail,
            signing_order: 1
          });
        }
        initialRecipients.push({
          id: createLocalId(),
          role: 'internal',
          full_name: staffDisplayName,
          email: staffEmail,
          signing_order: initialRecipients.length + 1
        });
        setRecipientRows(initialRecipients);

        if (tplRes.data?.length && !selectedTemplateId) {
          setSelectedTemplateId(tplRes.data[0].id);
        }
      } catch (err) {
        console.error('Agreement builder load error', err);
        setErrors([err.message || 'Failed to load agreement builder data.']);
        setLoadStatus((prev) =>
          Object.keys(prev).reduce(
            (acc, key) => ({ ...acc, [key]: prev[key] === 'loaded' ? prev[key] : 'error' }),
            {}
          )
        );
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, clientIdFromQuery]);

  useEffect(() => {
    const loadSections = async () => {
      if (!selectedTemplateId) {
        setSections([]);
        setOverriddenVariables({});
        return;
      }
      try {
        setSectionsLoading(true);
        const { data, error } = await supabase
          .from('agreement_template_sections')
          .select('*')
          .eq('template_id', selectedTemplateId)
          .order('position');
        if (error) throw error;
        const normalized = (data || []).map((section, index) => ({
          id: section.id,
          template_id: section.template_id,
          position: section.position ?? index,
          title: section.title || `Section ${index + 1}`,
          content: section.content || '',
          clause_refs: section.clause_refs || [],
          included: true
        }));
        setSections(normalized);

        // When template changes, reset overrides? Or keep them?
        // Let's reset cleanly to avoid stale variables from previous template.
        setOverriddenVariables({});

      } catch (err) {
        console.error('Template sections load error', err);
        setErrors((prev) => [...(prev || []), err.message || 'Unable to load template sections.']);
        setSections([]);
      } finally {
        setSectionsLoading(false);
      }
    };

    loadSections();
  }, [selectedTemplateId]);

  // CHANGED: Removed the recursive useEffect hooks that were causing flickering.
  // We now calculate 'variables' solely during render.

  const mergedVariables = useMemo(() => {
    // 1. Identify all required variables from the current sections
    const templateVarKeys = new Set();
    sections.forEach((section) => {
      if (!section.included) return;
      const matches = section.content.matchAll(/\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g);
      for (const match of matches) {
        if (match[1]) templateVarKeys.add(match[1]);
      }
    });

    const result = {};
    templateVarKeys.forEach((key) => {
      // 2. Prioritize user override, else fall back to derived default
      if (overriddenVariables[key] !== undefined) {
        result[key] = overriddenVariables[key];
      } else {
        result[key] = deriveVariableDefault(key);
      }
    });
    return result;
  }, [sections, overriddenVariables, deriveVariableDefault]);

  const compiledSections = useMemo(() => {
    return sections
      .filter((section) => section.included)
      .map((section, index) => {
        let compiled = section.content || '';
        Object.entries(mergedVariables || {}).forEach(([key, value]) => {
          const pattern = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
          compiled = compiled.replace(pattern, value ?? '');
        });
        return {
          ...section,
          compiledContent: compiled,
          renderedPosition: index
        };
      });
  }, [sections, mergedVariables]);

  const handleAgreementMetaChange = (field, value) => {
    setAgreementMeta((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSectionToggle = (id) => {
    setSections((prev) =>
      prev.map((section) => (
        section.id === id ? { ...section, included: !section.included } : section
      ))
    );
  };

  const handleSectionContentChange = (id, field, value) => {
    setSections((prev) =>
      prev.map((section) => (
        section.id === id ? { ...section, [field]: value } : section
      ))
    );
  };

  const handleVariableChange = (key, value) => {
    setOverriddenVariables((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRecipientChange = (id, field, value) => {
    setRecipientRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddRecipient = () => {
    setRecipientRows((prev) => [
      ...prev,
      {
        id: createLocalId(),
        role: 'client',
        full_name: '',
        email: '',
        signing_order: prev.length + 1
      }
    ]);
  };

  const handleRemoveRecipient = (id) => {
    setRecipientRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleAddPackageItem = () => {
    if (!selectedPackageId) return;
    const pkg = packages.find((p) => p.id === selectedPackageId);
    if (!pkg) return;
    const unitPrice = Number(pkg.price_range_min || pkg.price_range_max || 0);
    setItems((prev) => [
      ...prev,
      {
        id: createLocalId(),
        type: 'package',
        refId: pkg.id,
        title: pkg.package_name || pkg.package_key || 'Package',
        quantity: 1,
        unitPrice,
        currency: pkg.currency || agreementMeta.currency,
        metadata: {
          price_range_min: pkg.price_range_min,
          price_range_max: pkg.price_range_max,
          package_key: pkg.package_key
        }
      }
    ]);
    setSelectedPackageId('');
  };

  const handleAddLoanItem = () => {
    if (!selectedLoanId) return;
    const loan = loanOptions.find((l) => l.id === selectedLoanId);
    if (!loan) return;
    setItems((prev) => [
      ...prev,
      {
        id: createLocalId(),
        type: 'loan',
        refId: loan.id,
        title: `${loan.loan_provider} — ${loan.loan_type}`,
        quantity: 1,
        unitPrice: Number(loan.max_loan_amount || 0),
        currency: agreementMeta.currency,
        metadata: {
          interest_rate: loan.interest_rate,
          repayment_period_years: loan.repayment_period_years,
          requirements: loan.requirements
        }
      }
    ]);
    setSelectedLoanId('');
  };

  const handleAddCustomItem = () => {
    if (!customItem.title.trim()) {
      setErrors(['Custom item requires a title.']);
      return;
    }
    const quantity = Number(customItem.quantity) || 1;
    const unitPrice = Number(customItem.unitPrice) || 0;
    setItems((prev) => [
      ...prev,
      {
        id: createLocalId(),
        type: 'custom',
        refId: null,
        title: customItem.title.trim(),
        quantity,
        unitPrice,
        currency: agreementMeta.currency,
        metadata: customItem.description ? { description: customItem.description } : null
      }
    ]);
    setCustomItem({
      title: '',
      quantity: 1,
      unitPrice: '',
      description: ''
    });
    setErrors([]);
  };

  const handleItemFieldChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        if (field === 'quantity') {
          return { ...item, quantity: Number(value) || 0 };
        }
        if (field === 'unitPrice') {
          return { ...item, unitPrice: Number(value) || 0 };
        }
        return { ...item, [field]: value };
      })
    );
  };

  const handleRemoveItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleAddClauseToSections = (clause) => {
    if (!clause) return;
    setSections((prev) => [
      ...prev,
      {
        id: createLocalId(),
        template_id: null,
        position: prev.length,
        title: clause.title || clause.clause_key || 'Clause',
        content: clause.content || '',
        clause_refs: [clause.clause_key],
        included: true,
        metadata: { clause_id: clause.id }
      }
    ]);
  };

  const validateBeforeSave = () => {
    const problems = [];
    if (!selectedTemplateId) {
      problems.push('Select a template to continue.');
    }
    if (!agreementMeta.title.trim()) {
      problems.push('Agreement title is required.');
    }
    if (!items.length) {
      problems.push('Add at least one financial item to calculate the agreement total.');
    }
    if (!recipientRows.some((rec) => rec.email && rec.full_name)) {
      problems.push('Add at least one recipient with name and email.');
    }
    if (!sections.some((section) => section.included)) {
      problems.push('Include at least one section in the agreement content.');
    }
    const invalidVariables = Object.entries(mergedVariables || {})
      .filter(([key, value]) => (value ?? '').toString().trim() === '')
      .map(([key]) => key);
    if (invalidVariables.length) {
      problems.push(`Fill in all template variables. Missing: ${invalidVariables.join(', ')}`);
    }
    setErrors(problems);
    return problems.length === 0;
  };

  const handleCreateAgreement = async () => {
    setErrors([]);
    setInfoMessage('');
    if (!validateBeforeSave()) return;

    const sanitizedItems = items.map((item, index) => ({
      agreement_id: null, // placeholder; set later
      item_type: item.type,
      ref_id: item.refId || null,
      title: item.title || 'Line item',
      quantity: Number(item.quantity) || 0,
      unit_price: Number(item.unitPrice) || 0,
      subtotal: Number((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)),
      sort_order: index,
      metadata: item.metadata || null
    }));

    const resolvedRecipients = recipientRows
      .filter((rec) => (rec.email || '').trim() && (rec.full_name || '').trim())
      .map((rec, index) => ({
        agreement_id: null, // placeholder; set later
        role: rec.role || 'client',
        full_name: rec.full_name.trim(),
        email: rec.email.trim(),
        signing_order: Number(rec.signing_order) || index + 1,
        status: 'pending'
      }));

    setSaving(true);
    try {
      const agreementPayload = {
        title: agreementMeta.title.trim(),
        status: 'draft',
        currency: agreementMeta.currency,
        total_amount: Number(totalAmount) || 0,
        submission_id: submission?.id || null,
        client_id: client?.id || submission?.client_id || null,
        case_id: caseContext?.id || caseIdFromQuery || null,
        pathway_id: caseContext?.pathway_id || caseContext?.immigration_pathways?.id || pathwayIdFromQuery || submission?.pathway_id || null,
        valid_until: agreementMeta.validUntil || null,
        acceptance_deadline: agreementMeta.acceptance_deadline || null,
        payment_deadline: agreementMeta.payment_deadline || null
      };

      let agreementInsert = await supabase
        .from('agreements')
        .insert([agreementPayload])
        .select()
        .single();

      if (agreementInsert.error && agreementInsert.error.message?.includes('valid_until')) {
        delete agreementPayload.valid_until;
        agreementInsert = await supabase
          .from('agreements')
          .insert([agreementPayload])
          .select()
          .single();
      }

      if (agreementInsert.error) throw agreementInsert.error;
      const agreement = agreementInsert.data;

      const versionSections = sections
        .filter((section) => section.included)
        .map((section, index) => ({
          section_id: section.id,
          template_section_id: section.template_id,
          position: index,
          title: section.title,
          content: section.content,
          clause_refs: section.clause_refs || []
        }));

      const versionPayload = {
        agreement_id: agreement.id,
        template_id: selectedTemplateId,
        version_number: 1,
        status: 'draft',
        prepared_by: profile?.id || null,
        sections: versionSections,
        variables: {
          ...mergedVariables,
          acceptance_deadline: agreementMeta.acceptance_deadline || mergedVariables.acceptance_deadline || '',
          payment_deadline: agreementMeta.payment_deadline || mergedVariables.payment_deadline || ''
        },
        notes: agreementMeta.notes || null
      };

      let versionInsert = await supabase
        .from('agreement_versions')
        .insert([versionPayload]);

      if (versionInsert.error && versionInsert.error.message?.includes('prepared_by')) {
        delete versionPayload.prepared_by;
        versionInsert = await supabase
          .from('agreement_versions')
          .insert([versionPayload]);
      }
      if (versionInsert.error) throw versionInsert.error;

      if (sanitizedItems.length) {
        const itemPayloads = sanitizedItems.map((item) => ({
          ...item,
          agreement_id: agreement.id
        }));
        const itemsInsert = await supabase
          .from('agreement_items')
          .insert(itemPayloads);
        if (itemsInsert.error) throw itemsInsert.error;
      }

      if (resolvedRecipients.length) {
        const recipientPayloads = resolvedRecipients.map((rec) => ({
          ...rec,
          agreement_id: agreement.id
        }));
        const recipientsInsert = await supabase
          .from('agreement_recipients')
          .insert(recipientPayloads);
        if (recipientsInsert.error) throw recipientsInsert.error;
      }

      const actorEmail = profile?.email || profile?.work_email || user?.email || 'system';
      const eventsInsert = await supabase.from('agreement_events').insert([{
        agreement_id: agreement.id,
        event_type: 'created',
        actor: actorEmail,
        metadata: {
          template_id: selectedTemplateId,
          total_amount: Number(totalAmount) || 0
        }
      }]);
      if (eventsInsert.error) {
        console.warn('Agreement event insert failed', eventsInsert.error);
      }

      setInfoMessage('Agreement created successfully.');
      navigate(`/admin/agreements/${agreement.id}`);
    } catch (err) {
      console.error('Agreement creation error', err);
      setErrors([err.message || 'Failed to create agreement.']);
    } finally {
      setSaving(false);
    }
  };

  const filteredClauses = useMemo(() => {
    const q = clauseSearch.trim().toLowerCase();
    if (!q) return clauses;
    return clauses.filter((clause) => {
      const haystack = `${clause.title || ''} ${clause.category || ''} ${clause.content || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [clauses, clauseSearch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 font-mono text-white p-6 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">New Agreement</h1>
          <p className="text-sm text-gray-400">
            Assemble a professional agreement using templates, clauses, and client data. Fill the variables,
            confirm recipients, and publish when ready.
          </p>
          <div className="mt-3 text-xs text-gray-400 space-y-1">
            {client && (
              <div>
                <span className="font-semibold text-gray-300">Client: </span>
                {[client.first_name, client.last_name].filter(Boolean).join(' ') || 'Unknown'}
                {client.primary_email && (
                  <span className="text-gray-500"> · {client.primary_email}</span>
                )}
              </div>
            )}
            {!client && submission && (
              <div>
                <span className="font-semibold text-gray-300">Lead: </span>
                {submission.personal_info?.fullName ||
                  submission.personal_info?.name ||
                  'Unknown'}
              </div>
            )}
            {submission && (
              <div className="text-gray-500">
                From submission <span className="font-mono">{submission.id.slice(0, 8)}</span>{' '}
                · {submission.created_at ? new Date(submission.created_at).toLocaleDateString() : ''}
              </div>
            )}
            {caseContext && (
              <div>
                <span className="font-semibold text-gray-300">Case: </span>
                {caseContext.title || caseContext.id}
                {caseContext.clients?.first_name || caseContext.clients?.last_name ? (
                  <span className="text-gray-500">
                    {' '}· {[caseContext.clients?.first_name, caseContext.clients?.last_name].filter(Boolean).join(' ')}
                  </span>
                ) : null}
              </div>
            )}
            {caseContext?.immigration_pathways && (
              <div className="text-gray-500">
                {caseContext.immigration_pathways.countries?.flag_emoji || ''}{' '}
                {WizardService.formatPathwayName(caseContext.immigration_pathways.pathway_key)}
                {caseContext.immigration_pathways.countries?.name ? ` · ${caseContext.immigration_pathways.countries.name}` : ''}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleCreateAgreement}
            disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded shadow-sm hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {saving ? 'Creating…' : 'Create Agreement'}
          </button>
          <button
            onClick={() => navigate('/admin/agreements')}
            className="px-4 py-2 text-sm border border-gray-600 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded space-y-2">
          <div className="text-sm font-medium">Please fix the following:</div>
          <ul className="list-disc list-inside text-sm space-y-1">
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {infoMessage && (
        <div className="bg-green-900/20 border border-green-800 text-green-400 px-4 py-3 rounded text-sm">
          {infoMessage}
        </div>
      )}

      <div className="text-xs text-gray-500 bg-gray-800 border border-gray-700 rounded px-3 py-2">
        Supabase URL in use: <span className="text-gray-300">{resolvedSupabaseConfig.supabaseUrl}</span>
        {resolvedSupabaseConfig.usingFallback && (
          <span className="text-yellow-300 ml-2">[Fallback: set REACT_APP_SUPABASE_URL/ANON_KEY]</span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-300 mb-4">
        {Object.entries(loadStatus).map(([key, status]) => (
          <div key={key} className="border border-gray-700 rounded px-3 py-2 bg-gray-800">
            <div className="uppercase tracking-wide text-gray-500">{key}</div>
            <div className={
              status === 'loaded' ? 'text-green-400' :
                status === 'loading' ? 'text-yellow-300' :
                  status === 'error' ? 'text-red-400' : 'text-gray-400'
            }>
              {status}
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4 lg:col-span-2">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-400">Agreement Title</label>
              <input
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={agreementMeta.title}
                onChange={(e) => handleAgreementMetaChange('title', e.target.value)}
                placeholder="e.g. Ravana Migration Services Agreement"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-400">Currency</label>
              <select
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={agreementMeta.currency}
                onChange={(e) => handleAgreementMetaChange('currency', e.target.value)}
              >
                {CURRENCY_OPTIONS.map((currency) => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-400">Valid Until</label>
              <input
                type="date"
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={agreementMeta.validUntil}
                onChange={(e) => handleAgreementMetaChange('validUntil', e.target.value)}
              />
              <div className="flex gap-2 text-xs text-gray-400">
                <button
                  type="button"
                  className="px-2 py-1 border border-gray-600 rounded bg-gray-700 hover:border-blue-500"
                  onClick={() => {
                    const date = new Date(); date.setDate(date.getDate() + 7);
                    handleAgreementMetaChange('validUntil', date.toISOString().slice(0, 10));
                  }}
                >
                  +7d
                </button>
                <button
                  type="button"
                  className="px-2 py-1 border border-gray-600 rounded bg-gray-700 hover:border-blue-500"
                  onClick={() => {
                    const date = new Date(); date.setDate(date.getDate() + 14);
                    handleAgreementMetaChange('validUntil', date.toISOString().slice(0, 10));
                  }}
                >
                  +14d
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-400">Acceptance Deadline</label>
              <input
                type="date"
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={agreementMeta.acceptance_deadline}
                onChange={(e) => handleAgreementMetaChange('acceptance_deadline', e.target.value)}
              />
              <div className="flex gap-2 text-xs text-gray-400">
                <button
                  type="button"
                  className="px-2 py-1 border border-gray-600 rounded bg-gray-700 hover:border-blue-500"
                  onClick={() => {
                    const date = new Date(); date.setDate(date.getDate() + 7);
                    handleAgreementMetaChange('acceptance_deadline', date.toISOString().slice(0, 10));
                  }}
                >
                  +7d
                </button>
                <button
                  type="button"
                  className="px-2 py-1 border border-gray-600 rounded bg-gray-700 hover:border-blue-500"
                  onClick={() => {
                    const date = new Date(); date.setDate(date.getDate() + 14);
                    handleAgreementMetaChange('acceptance_deadline', date.toISOString().slice(0, 10));
                  }}
                >
                  +14d
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-400">Payment Deadline</label>
              <input
                type="date"
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={agreementMeta.payment_deadline}
                onChange={(e) => handleAgreementMetaChange('payment_deadline', e.target.value)}
              />
              <div className="flex gap-2 text-xs text-gray-400">
                <button
                  type="button"
                  className="px-2 py-1 border border-gray-600 rounded bg-gray-700 hover:border-blue-500"
                  onClick={() => {
                    const date = new Date(); date.setDate(date.getDate() + 7);
                    handleAgreementMetaChange('payment_deadline', date.toISOString().slice(0, 10));
                  }}
                >
                  +7d
                </button>
                <button
                  type="button"
                  className="px-2 py-1 border border-gray-600 rounded bg-gray-700 hover:border-blue-500"
                  onClick={() => {
                    const date = new Date(); date.setDate(date.getDate() + 14);
                    handleAgreementMetaChange('payment_deadline', date.toISOString().slice(0, 10));
                  }}
                >
                  +14d
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-400">Notes (internal)</label>
              <input
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={agreementMeta.notes}
                onChange={(e) => handleAgreementMetaChange('notes', e.target.value)}
                placeholder="Optional notes for this agreement version"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-white">Template & Sections</h2>
                <p className="text-xs text-gray-400">
                  Select a base template then fine-tune sections or add clauses.
                </p>
              </div>
              <select
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={selectedTemplateId || ''}
                onChange={(e) => setSelectedTemplateId(e.target.value || null)}
              >
                {(!templates || templates.length === 0) && (
                  <option value="">No templates available</option>
                )}
                {(templates || []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.title || 'Untitled Template'}
                  </option>
                ))}
              </select>
            </div>

            {sectionsLoading ? (
              <div className="py-6 flex items-center justify-center text-sm text-gray-400">
                Loading sections…
              </div>
            ) : (
              <div className="space-y-3">
                {sections.map((section) => (
                  <div key={section.id} className="border border-gray-600 rounded bg-gray-700/50 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                          Section {section.position + 1}
                        </div>
                        <input
                          className="text-sm font-medium text-white border-b border-transparent bg-transparent focus:border-gray-500 focus:outline-none"
                          value={section.title}
                          onChange={(e) => handleSectionContentChange(section.id, 'title', e.target.value)}
                        />
                      </div>
                      <label className="text-xs flex items-center gap-1 text-gray-400">
                        <input
                          type="checkbox"
                          checked={section.included}
                          onChange={() => handleSectionToggle(section.id)}
                          className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                        Include
                      </label>
                    </div>
                    <textarea
                      className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-sm text-white w-full h-32 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                      value={section.content}
                      onChange={(e) => handleSectionContentChange(section.id, 'content', e.target.value)}
                    />
                    {section.clause_refs?.length > 0 && (
                      <div className="text-xs text-gray-400">
                        Clauses: {section.clause_refs.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
                {sections.length === 0 && (
                  <div className="text-sm text-gray-500">No sections configured for this template yet.</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          <div>
            <h2 className="text-lg font-medium text-white">Template Variables</h2>
            <p className="text-xs text-gray-400">
              Fill the placeholders referenced inside the template. These values merge into the preview.
            </p>
          </div>
          <div className="space-y-3 text-sm">
            {Object.keys(mergedVariables).length === 0 && (
              <div className="text-sm text-gray-500">Select a template to see variables.</div>
            )}
            {Object.entries(mergedVariables).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-gray-400">
                  {key}
                </label>
                <input
                  className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={value ?? ''}
                  onChange={(e) => handleVariableChange(key, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-white">Recipients & Signers</h2>
              <p className="text-xs text-gray-400">
                Define who receives the agreement and the signing order.
              </p>
            </div>
            <button
              onClick={handleAddRecipient}
              className="px-3 py-1 text-xs border border-gray-600 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              Add Recipient
            </button>
          </div>
          <div className="space-y-3 text-sm">
            {recipientRows.map((recipient) => (
              <div key={recipient.id} className="border border-gray-600 rounded bg-gray-700/50 px-4 py-3 grid md:grid-cols-5 gap-2">
                <input
                  className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-sm text-white md:col-span-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Full name"
                  value={recipient.full_name}
                  onChange={(e) => handleRecipientChange(recipient.id, 'full_name', e.target.value)}
                />
                <input
                  className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-sm text-white md:col-span-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Email"
                  value={recipient.email}
                  onChange={(e) => handleRecipientChange(recipient.id, 'email', e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <select
                    className="border border-gray-600 bg-gray-700 rounded px-2 py-2 text-xs text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={recipient.role}
                    onChange={(e) => handleRecipientChange(recipient.id, 'role', e.target.value)}
                  >
                    <option value="client">Client</option>
                    <option value="co-signer">Co-Signer</option>
                    <option value="internal">Team</option>
                  </select>
                  <input
                    className="border border-gray-600 bg-gray-700 rounded px-2 py-2 text-xs text-white w-16 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="number"
                    value={recipient.signing_order}
                    onChange={(e) => handleRecipientChange(recipient.id, 'signing_order', e.target.value)}
                  />
                  <button
                    onClick={() => handleRemoveRecipient(recipient.id)}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {recipientRows.length === 0 && (
              <div className="text-sm text-gray-500">Add at least one recipient.</div>
            )}
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          <div>
            <h2 className="text-lg font-medium text-white">Clause Library</h2>
            <p className="text-xs text-gray-400">
              Insert standard clauses to the current agreement draft.
            </p>
          </div>
          <input
            className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-sm text-white w-full placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Search clauses"
            value={clauseSearch}
            onChange={(e) => setClauseSearch(e.target.value)}
          />
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1 text-sm">
            {filteredClauses.map((clause) => (
              <div key={clause.id} className="border border-dashed border-gray-600 rounded bg-gray-700/30 px-3 py-2 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white text-sm">{clause.title}</div>
                  <button
                    onClick={() => handleAddClauseToSections(clause)}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    Add
                  </button>
                </div>
                <div className="text-xs text-gray-400">Key: {clause.clause_key}</div>
                {clause.category && (
                  <div className="text-xs text-gray-500">Category: {clause.category}</div>
                )}
              </div>
            ))}
            {filteredClauses.length === 0 && (
              <div className="text-xs text-gray-500">No clauses match the search.</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-white">Financial Items</h2>
            <p className="text-xs text-gray-400">
              Combine packages, loans, and custom line items. Totals update automatically.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wide text-gray-400">Add package</label>
            <div className="flex items-center gap-2">
              <select
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={selectedPackageId}
                onChange={(e) => setSelectedPackageId(e.target.value)}
              >
                <option value="">Select package</option>
                {packages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.immigration_pathways?.countries?.flag_emoji
                      ? `${pkg.immigration_pathways.countries.flag_emoji} `
                      : ''}
                    {pkg.package_name || pkg.package_key}
                    {pkg.immigration_pathways?.pathway_key
                      ? ` – ${pkg.immigration_pathways.pathway_key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}`
                      : ''}
                    {pkg.immigration_pathways?.countries?.name
                      ? ` · ${pkg.immigration_pathways.countries.name}`
                      : ''}
                    {` (${pkg.currency || agreementMeta.currency})`}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddPackageItem}
                className="px-3 py-2 text-xs border border-gray-600 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wide text-gray-400">Add loan option</label>
            <div className="flex items-center gap-2">
              <select
                className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={selectedLoanId}
                onChange={(e) => setSelectedLoanId(e.target.value)}
              >
                <option value="">Select loan</option>
                {loanOptions.map((loan) => (
                  <option key={loan.id} value={loan.id}>
                    {loan.loan_provider} — {loan.loan_type}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddLoanItem}
                className="px-3 py-2 text-xs border border-gray-600 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="border border-gray-600 rounded bg-gray-700/30 px-4 py-3 space-y-2">
          <label className="text-xs uppercase tracking-wide text-gray-400">Custom line item</label>
          <div className="grid md:grid-cols-4 gap-2 text-sm">
            <input
              className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white md:col-span-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Description"
              value={customItem.title}
              onChange={(e) => setCustomItem((prev) => ({ ...prev, title: e.target.value }))}
            />
            <input
              type="number"
              className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Quantity"
              min="0"
              value={customItem.quantity}
              onChange={(e) => setCustomItem((prev) => ({ ...prev, quantity: e.target.value }))}
            />
            <input
              type="number"
              className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Unit price"
              min="0"
              value={customItem.unitPrice}
              onChange={(e) => setCustomItem((prev) => ({ ...prev, unitPrice: e.target.value }))}
            />
            <textarea
              className="border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white md:col-span-4 h-16 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Notes (optional)"
              value={customItem.description}
              onChange={(e) => setCustomItem((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <button
            onClick={handleAddCustomItem}
            className="px-3 py-2 text-xs border border-gray-600 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
          >
            Add custom item
          </button>
        </div>

        <div className="overflow-x-auto rounded border border-gray-600">
          <table className="min-w-full divide-y divide-gray-700 text-sm">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Title</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">Quantity</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">Unit price</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">Subtotal</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2">
                    <input
                      className="border border-gray-600 bg-gray-700 rounded px-2 py-1 text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={item.title}
                      onChange={(e) => handleItemFieldChange(item.id, 'title', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400 capitalize">{item.type}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end">
                      <input
                        type="number"
                        min="0"
                        className="border border-gray-600 bg-gray-700 rounded px-2 py-1 text-white w-20 text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={item.quantity}
                        onChange={(e) => handleItemFieldChange(item.id, 'quantity', e.target.value)}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end">
                      <input
                        type="number"
                        min="0"
                        className="border border-gray-600 bg-gray-700 rounded px-2 py-1 text-white w-28 text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={item.unitPrice}
                        onChange={(e) => handleItemFieldChange(item.id, 'unitPrice', e.target.value)}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-white text-right font-mono">
                    {(Number(item.quantity || 0) * Number(item.unitPrice || 0)).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-sm text-gray-500">
                    No items yet. Add packages, loans, or custom lines above.
                  </td>
                </tr>
              )}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-gray-900 border-t border-gray-700 font-bold">
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-right text-gray-300">
                    Agreement Total ({agreementMeta.currency})
                  </td>
                  <td className="px-3 py-3 text-right text-white font-mono text-base">
                    {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-white">Preview</h2>
            <p className="text-xs text-gray-400">
              Rendered sections with variables applied. Use this to review before sending.
            </p>
          </div>
          <div className="text-xs text-gray-400">
            {compiledSections.length} section{compiledSections.length === 1 ? '' : 's'}
          </div>
        </div>
        <div className="space-y-4">
          {compiledSections.map((section) => (
            <article key={section.id} className="border border-gray-600 rounded bg-gray-700/30 px-4 py-3 space-y-2">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Section {section.renderedPosition + 1}
              </div>
              <h3 className="text-sm font-semibold text-white">{section.title}</h3>
              <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed font-mono">
                {section.compiledContent}
              </div>
            </article>
          ))}
          {compiledSections.length === 0 && (
            <div className="text-sm text-gray-500">Include sections to preview agreement content.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgreementsBuilderPage;
